import React, {useEffect, useRef, useState} from 'react';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useAuth} from '@/hooks/useAuth';
import {Header} from '@/components/Header';
import {Home, Plus, Search, User} from 'lucide-react';
import {motion} from 'framer-motion';
import YellowPageOnboardingForm from "@/components/YellowPageOnboardingForm";

const TABS = [
	{id: 'home', path: '/yellow-pages', icon: Home, labelKey: 'YELLOW_PAGE.NAV.HOME'},
	{id: 'search', path: '/yellow-pages/search', icon: Search, labelKey: 'YELLOW_PAGE.NAV.DISCOVER'},
	{id: 'profile', path: '/yellow-pages/my-profile', icon: User, labelKey: 'YELLOW_PAGE.NAV.PROFILE'},
];

export const YellowPagesLayout = () => {
	const {isAuthenticated, account} = useAuth();

	const onboardingRafRef = useRef(null);
	const [showOnboarding, setShowOnboarding] = useState(false);
	const [onboardingVisible, setOnboardingVisible] = useState(false);

	const openOnboarding = () => {
		setShowOnboarding(true);
		cancelAnimationFrame(onboardingRafRef.current);
		onboardingRafRef.current = requestAnimationFrame(() => {
			onboardingRafRef.current = requestAnimationFrame(() =>
				setOnboardingVisible(true));
		});
	};

	const closeOnboarding = () => {
		setOnboardingVisible(false);
		setTimeout(() => setShowOnboarding(false), 220);
	};

	useEffect(() => () => cancelAnimationFrame(onboardingRafRef.current), []);

	const {t} = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const activeTab = TABS.find(tab => location.pathname === tab.path)?.id
		|| (location.pathname.startsWith('/yellow-pages/search') ? 'search' : 'home');

	return (
		<div
			className="min-h-[100dvh] h-[100dvh] max-h-[100dvh]
                min-w-full w-full max-w-full bg-white flex flex-col"
		>
			<Header/>
			<div
				className={`bg-orange-50 text-gray-800 flex-1 overflow-y-auto thin-scrollbar
			        portrait:pt-[4.75rem] portrait:sm:pt-[6.25rem] portrait:jk:pt-[5.75rem]
			        landscape:pt-[3.5rem] landscape:jk:pt-[4.5rem]
			        ${isAuthenticated ? 'pb-24' : 'pb-4'} px-3 sm:px-4`}
			>
				<Outlet/>
			</div>

			{isAuthenticated && (
				<nav
					className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50"
					role="navigation" aria-label={t('YELLOW_PAGE.NAV.ARIA_LABEL')}
				>
					<div
						className="flex items-center gap-0.5 bg-white/95 backdrop-blur-xl
                            border border-orange-100/60 rounded-full pl-1.5 pr-1 py-1.5"
						style={{boxShadow: '0 8px 32px rgba(194,65,12,0.10), 0 2px 8px rgba(0,0,0,0.06)'}}
					>
						{TABS.map(tab => {
							const Icon = tab.icon;
							const isActive = activeTab === tab.id;
							return (
								<button key={tab.id} onClick={() => navigate(tab.path)}
								        aria-label={t(tab.labelKey)}
								        aria-current={isActive ? 'page' : undefined}
								        className={`relative flex items-center justify-center gap-2
						                    rounded-full transition-all duration-300 ease-out
						                    ${isActive
									        ? 'bg-orange-600 text-white px-5 py-2.5 shadow-md'
									        : 'text-gray-400 hover:text-orange-500 px-3.5 py-2.5'}`}
								        style={isActive ? {boxShadow: '0 4px 14px rgba(234,88,12,0.3)'} : {}}
								>
									<Icon className="w-[1.15rem] h-[1.15rem]" strokeWidth={isActive ? 2.5 : 2}/>
									{isActive && (
										<motion.span
											initial={{width: 0, opacity: 0}}
											animate={{width: 'auto', opacity: 1}}
											exit={{width: 0, opacity: 0}}
											transition={{duration: 0.25, ease: 'easeOut'}}
											className="font-custom-bold text-[0.7rem] whitespace-nowrap overflow-hidden">
											{t(tab.labelKey)}
										</motion.span>
									)}
								</button>
							);
						})}

						<button
							onClick={() => openOnboarding()}
							aria-label={t('YELLOW_PAGE.NAV.LIST_NEW')}
							className="flex items-center justify-center w-11 h-11 rounded-full
				                bg-gray-900 text-white ml-1
				                hover:bg-gray-800 active:scale-95 transition-all"
							style={{boxShadow: '0 4px 14px rgba(0,0,0,0.25)'}}
						>
							<Plus className="w-5 h-5" strokeWidth={2.5}/>
						</button>
					</div>
				</nav>
			)}

			{showOnboarding && (
				<div
					className="fixed inset-0 z-[100] flex items-end sm:items-center
                        justify-center sm:p-4 transition-opacity duration-220"
					style={{
						background: "rgba(0,0,0,0.55)",
						backdropFilter: "blur(4px)",
						opacity: onboardingVisible ? 1 : 0,
					}}
					onClick={closeOnboarding}
				>
					<div
						className="w-full lg:w-[60rem] transition-all duration-220"
						style={{
							transform: onboardingVisible
								? "scale(1) translateY(0)"
								: "scale(0.97) translateY(1rem)",
							opacity: onboardingVisible ? 1 : 0,
						}}
						onClick={e => e.stopPropagation()}
					>
						<YellowPageOnboardingForm
							setIsYellowPageOnboardingFormModalOpen={closeOnboarding}
							accountDetails={account}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default YellowPagesLayout;